/**
 * Mirrors backend RangeSliderProcessor + dimension aggregation (content-management).
 * Used when results.dimensions is missing so charts match evaluated scores.
 */

/** Likert minimum when a dimension has only non-sticky rangeSlider questions with no answers. */
export const LIKERT_DIMENSION_EMPTY_FALLBACK = 1;

/** @param {number} value */
export function flipNegativeMindsetValue(value) {
  return 1 + Math.abs(5 - value);
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isMissingRangeSliderValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (typeof value === 'number' && Number.isNaN(value)) return true;
  return Number.isNaN(Number(value));
}

/**
 * @param {string|undefined} questionType
 * @param {unknown} answer
 * @returns {unknown}
 */
export function normalizeRangeSliderAnswerForStorage(questionType, answer) {
  if (questionType !== 'rangeSlider') return answer;
  if (isMissingRangeSliderValue(answer)) return Number.NaN;
  return answer;
}

/**
 * @param {object|undefined} data - question.properties.data
 * @returns {number}
 */
function rangeSliderProcessedValue(data) {
  if (!data || isMissingRangeSliderValue(data.value)) return Number.NaN;
  let pos = true;
  if (Object.prototype.hasOwnProperty.call(data, 'positive')) {
    pos = data.positive ?? true;
  } else if (Object.prototype.hasOwnProperty.call(data, 'valuePositive')) {
    pos = data.valuePositive ?? true;
  }
  const v = Number(data.value);
  if (Number.isNaN(v)) return Number.NaN;
  return pos ? v : flipNegativeMindsetValue(v);
}

/**
 * Mean of all non-sticky rangeSlider answers for a dimension (same formula as backend).
 * @param {Array} assessmentQuestionArray
 * @param {string} dimensionKey
 * @returns {number|null}
 */
export function rangeSliderMeanForDimension(assessmentQuestionArray, dimensionKey) {
  const questions = (assessmentQuestionArray || []).filter(
    (q) => !q.sticky
      && q.type === 'rangeSlider'
      && q.properties?.dimensionName === dimensionKey,
  );
  if (questions.length === 0) return null;

  const values = questions
    .map((q) => rangeSliderProcessedValue(q.properties?.data))
    .filter((v) => !Number.isNaN(v));
  if (values.length === 0) return LIKERT_DIMENSION_EMPTY_FALLBACK;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Per-dimension means for rangeSlider questions only (fallback when results.dimensions absent).
 * @param {Array} assessmentQuestionArray
 * @returns {Record<string, number>}
 */
export function dimensionScoresFromRangeSliderQuestions(assessmentQuestionArray) {
  const byDim = new Map();
  (assessmentQuestionArray || []).forEach((q) => {
    if (q.sticky || q.type !== 'rangeSlider') return;
    const dim = q.properties?.dimensionName;
    if (!dim) return;
    if (!byDim.has(dim)) byDim.set(dim, []);
    const v = rangeSliderProcessedValue(q.properties?.data);
    if (!Number.isNaN(v)) byDim.get(dim).push(v);
  });
  /** @type {Record<string, number>} */
  const out = {};
  byDim.forEach((vals, dim) => {
    out[dim] = vals.length > 0
      ? vals.reduce((a, b) => a + b, 0) / vals.length
      : LIKERT_DIMENSION_EMPTY_FALLBACK;
  });
  return out;
}
